/**
 * t171u: bug reports and feature suggestions. Anyone sends one from Help ("Report Bug / Suggest Feature"); it
 * becomes a task in the workspace's Hub feedback project. Reporters follow theirs under Help › My submissions and
 * reply to the admins in its comments; admins (feedback.manage) work them on the normal board.
 */
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bug, Camera, Lightbulb, Link2, MessageSquare, X } from 'lucide-react';
import { FEEDBACK_KIND_LABEL, FEEDBACK_KINDS, type FeedbackDetail, type FeedbackItem, type FeedbackKind } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { discardUpload, openAttachment, uploadFile } from '@/lib/upload';
import { Button, Center, EmptyState, Input, Modal, NotFound, Spinner, Textarea } from '@/components/ui';
import { RelativeTime } from '@/components/time/RelativeTime';
import { PersonButton } from '@/features/people/PersonDrawer';
import { StatusDot } from '@/features/projects/taskUi';
import { useAuth } from '@/lib/auth';
import { TaskCommentItem } from '@/features/tasks/TaskCommentItem';
import { useTaskCommentActions } from '@/features/tasks/taskCommentActions';
import { enterToSend } from '@/lib/enterToSend';
import { REPORT_LABEL } from './label';

export const feedbackKeys = { mine: (ws: string) => ['feedback', ws, 'mine'] as const, one: (id: string) => ['feedback', 'one', id] as const };
const chip = (on: boolean) => clsx('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ring-1', on ? 'bg-accent font-semibold text-on-accent ring-ink' : 'ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800');
const KindIcon = ({ kind, className = 'h-3.5 w-3.5' }: { kind: FeedbackKind; className?: string }) => (kind === 'bug' ? <Bug className={className} aria-hidden /> : <Lightbulb className={className} aria-hidden />);
export const appVersion = () => String(import.meta.env.VITE_APP_VERSION ?? import.meta.env.MODE ?? '');

/** t171u: Bug / Feature on board cards and list rows. */
export function FeedbackKindChip({ kind }: { kind?: FeedbackKind | null }) {
  if (!kind) return null;
  return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 dark:text-slate-200" data-testid="feedback-kind-chip"><KindIcon kind={kind} className="h-3 w-3" />{FEEDBACK_KIND_LABEL[kind]}</span>;
}

/** Kind, status and key as compact chips (lists and the submission header). */
function Chips({ f }: { f: Pick<FeedbackItem, 'kind' | 'status' | 'key'> }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-medium dark:bg-slate-800" data-testid="feedback-kind"><KindIcon kind={f.kind} />{FEEDBACK_KIND_LABEL[f.kind]}</span>
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-medium dark:bg-slate-800" data-testid="feedback-status"><StatusDot status={f.status} />{f.status.name}</span>
      <span className="font-mono text-slate-500">{f.key}</span>
    </span>
  );
}

/** "Report Bug / Suggest Feature": mounted once in the app shell, opened from Help. */
export function FeedbackDialog() {
  const open = useUi(s => s.feedbackOpen);
  const setOpen = useUi(s => s.openFeedback);
  return open ? <FeedbackForm onClose={() => setOpen(false)} /> : null;
}

function FeedbackForm({ onClose }: { onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const navigate = useNavigate();
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<{ id: string; name: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  // Captured when the dialog opens: the page you were on.
  const page = useRef(window.location.href).current;
  const close = () => { for (const f of files) discardUpload(f.id); onClose(); };
  const send = useMutation({
    mutationFn: () => api.feedback.submit(ws!, { kind, title: title.trim(), description: description.trim() || undefined, attachment_ids: files.map(f => f.id),
      page_url: page.slice(0, 2000), user_agent: navigator.userAgent.slice(0, 1000), app_version: appVersion().slice(0, 100) || undefined }),
    onSuccess: r => {
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
      toast(`Sent as ${r.key}`, 'success');
      onClose();
      navigate(`/help/feedback/${r.id}`);
    },
    onError: e => toast(errorMessage(e), 'error'),
  });
  const add = async (list: FileList | null) => {
    if (!list?.length || !ws) return;
    setUploading(true);
    try { for (const file of Array.from(list).slice(0, 10 - files.length)) { const a = await uploadFile(file, { workspace_id: ws, purpose: 'feedback' }); setFiles(x => [...x, { id: a.id, name: file.name }]); } }
    catch (e) { toast(errorMessage(e), 'error'); } finally { setUploading(false); }
  };
  return (
    <Modal open onClose={close} title={REPORT_LABEL}
      footer={<><Button variant="ghost" onClick={close}>Cancel</Button><Button onClick={() => send.mutate()} loading={send.isPending} disabled={!title.trim() || uploading} data-testid="feedback-send">Send</Button></>}>
      <div className="space-y-3" data-testid="feedback-dialog">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Type">
          {FEEDBACK_KINDS.map(k => <button key={k.key} type="button" role="radio" aria-checked={kind === k.key} className={chip(kind === k.key)} onClick={() => setKind(k.key)}><KindIcon kind={k.key} className="h-4 w-4" />{k.label}</button>)}
        </div>
        <Input aria-label="Title" placeholder={kind === 'bug' ? 'What went wrong?' : 'What would you like?'} maxLength={300} value={title} onChange={e => setTitle(e.target.value)} autoFocus />
        <Textarea aria-label="Description" rows={5} placeholder={kind === 'bug' ? 'What you did, what happened, what you expected' : 'How it would help'} maxLength={20000} value={description} onChange={e => setDescription(e.target.value)} />
        <div className="flex flex-wrap items-center gap-2">
          {files.map(x => <span key={x.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">{x.name}
            <button type="button" aria-label={`Remove ${x.name}`} onClick={() => { setFiles(fs => fs.filter(y => y.id !== x.id)); discardUpload(x.id); }}><X className="h-3 w-3" /></button></span>)}
          {files.length < 10 && <label className="inline-flex cursor-pointer items-center gap-1 rounded-full px-3 py-1 text-sm ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800">
            <Camera className="h-4 w-4" />{uploading ? 'Uploading…' : 'Screenshots'}<input type="file" accept="image/*" multiple className="hidden" data-testid="feedback-files" onChange={e => void add(e.target.files)} /></label>}
        </div>
        <p className="flex min-w-0 items-center gap-1 text-xs text-slate-500" data-testid="feedback-page"><Link2 className="h-3.5 w-3.5 shrink-0" aria-label="Page" /><span className="truncate">{pagePath(page)}</span></p>
      </div>
    </Modal>
  );
}

/** The captured page as shown in the dialog: path and query, without the origin. */
export function pagePath(href: string): string {
  try { const u = new URL(href); return `${u.pathname}${u.search}`; } catch { return href; }
}

/** Help › My submissions: your own reports and suggestions, newest first. */
export function MySubmissions() {
  const ws = useWorkspaceId();
  const openFeedback = useUi(s => s.openFeedback);
  const q = useQuery({ queryKey: feedbackKeys.mine(ws ?? ''), queryFn: () => api.feedback.mine(ws!), enabled: Boolean(ws) });
  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6" data-testid="feedback-mine">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="mr-auto text-xl font-semibold">My submissions</h1>
        {q.data?.project_id && <Link to={`/projects/${q.data.project_id}`} className="rounded-md px-2.5 py-1.5 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="feedback-board">All submissions</Link>}
        <Button size="sm" onClick={() => openFeedback(true)}><Bug className="h-4 w-4" />{REPORT_LABEL}</Button>
      </div>
      {q.isLoading ? <Center><Spinner /></Center> : !q.data?.items.length ? <EmptyState icon={<Lightbulb className="h-8 w-8" />} title="Nothing sent yet" /> : (
        <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
          {q.data.items.map(f => (
            <li key={f.id}><Link to={`/help/feedback/${f.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60" data-testid="feedback-row">
              <span className="min-w-0 flex-1"><span className="block truncate font-medium">{f.title}</span><span className="mt-1 block"><Chips f={f} /></span></span>
              {f.comment_count > 0 && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><MessageSquare className="h-3.5 w-3.5" aria-label="Comments" />{f.comment_count}</span>}
              <span className="shrink-0 text-xs text-slate-500"><RelativeTime iso={f.updated_at} /></span>
            </Link></li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One submission, as its reporter (or an admin) sees it: status, who's on it, and the conversation with the admins. */
export function SubmissionPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: feedbackKeys.one(id), queryFn: () => api.feedback.get(id), retry: false });
  if (q.isLoading) return <Center><Spinner /></Center>;
  if (!q.data) return <NotFound />;
  return <Submission f={q.data} />;
}

function Submission({ f }: { f: FeedbackDetail }) {
  const openTask = useUi(s => s.openTask);
  const toast = useUi(s => s.toast);
  const { byId } = useMembers();
  const comments = useQuery({ queryKey: ['task-comments', f.id], queryFn: () => api.taskComments(f.id) });
  const { userId } = useAuth();
  const commentActs = useTaskCommentActions(id => byId.get(id)?.display_name ?? 'someone'); // t258u
  const [draft, setDraft] = useState('');
  const send = useMutation({
    mutationFn: () => api.addTaskComment(f.id, { body: draft.trim() }),
    onSuccess: () => { setDraft(''); void comments.refetch(); void queryClient.invalidateQueries({ queryKey: ['feedback'] }); },
    onError: e => toast(errorMessage(e), 'error'),
  });
  const person = (p: { id: string; name: string }) => <PersonButton key={p.id} userId={p.id} name={p.name} className="rounded font-medium text-slate-700 hover:underline dark:text-slate-200">{p.name}</PersonButton>;
  const [body, footer] = f.description.split(/\n\n---\n/);
  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6" data-testid="feedback-submission">
      <Link to="/help/feedback" className="text-sm text-slate-600 hover:underline dark:text-slate-300">My submissions</Link>
      <div className="mt-2 flex flex-wrap items-start gap-2">
        <h1 className="mr-auto min-w-0 text-xl font-semibold">{f.title}</h1>
        {f.can_manage && <Button size="sm" variant="secondary" onClick={() => openTask(f.id)} data-testid="feedback-open-task">Open task</Button>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        <Chips f={f} />
        <span>From {person(f.reporter)} · <RelativeTime iso={f.created_at} /></span>
        {f.assignees.length > 0 && <span>With {f.assignees.map((a, i) => <span key={a.id}>{i > 0 && ', '}{person(a)}</span>)}</span>}
        {f.priority !== 'none' && <span className="capitalize">{f.priority}</span>}
      </div>
      {body && <div className="mt-4 whitespace-pre-wrap text-sm">{body}</div>}
      {f.attachments.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{f.attachments.map(a => (
        <button key={a.id} type="button" onClick={() => void openAttachment(a.id)} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700">{a.file_name}</button>))}</div>}
      {(f.page_url || f.browser || f.app_version) && (
        <p className="mt-3 flex flex-wrap gap-x-3 text-xs text-slate-500" data-testid="feedback-captured">
          {f.page_url && <span className="max-w-full truncate" title={f.page_url}>{f.page_url.replace(/^https?:\/\/[^/]+/, '')}</span>}
          {f.browser && <span>{f.browser}</span>}{f.app_version && <span>v {f.app_version}</span>}
        </p>
      )}
      {!f.page_url && footer && <p className="mt-3 whitespace-pre-wrap text-xs text-slate-500">{footer}</p>}
      <section className="mt-6 border-t border-slate-200 pt-4 dark:border-slate-800" aria-label="Comments" data-testid="feedback-comments">
        <div className="space-y-4">
          {comments.data?.comments.filter(c => !c.deleted_at).map(c => (
            <TaskCommentItem key={c.id} c={c} name={byId.get(c.author_id)?.display_name} mine={c.author_id === userId} act={commentActs} />
          ))}
        </div>
        <form className="mt-4 space-y-2" onSubmit={e => { e.preventDefault(); if (draft.trim()) send.mutate(); }}>
          <Textarea aria-label="Reply" rows={2} placeholder="Reply…" value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={enterToSend({ value: draft, onSend: () => send.mutate() })} />
          <div className="flex justify-end"><Button size="sm" type="submit" disabled={!draft.trim()} loading={send.isPending}>Reply</Button></div>
        </form>
      </section>
    </div>
  );
}
